// Reports → Products (F-ANL-03, spec/17 R-06): the best-selling food, drink and retail items (paid sales, before tax)
// as ranked bars, play sold as passes or as play time at the rate card (a donut), and the passes sold and granted.
// Every item and pass is in the table behind its chart. Needs `reports:financial`.

import type { ProductsReport } from '@clavian/api-client';
import { useTranslation, useVenueFormatter } from '@clavian/i18n/react';
import { BarChart, ChartEmpty, ChartTable, DonutChart, StatCard, type TableColumn } from '@clavian/ui-web';
import type { ReactNode } from 'react';

import { KpiFigure, ReportCard, ReportGrid, ReportKpis, ReportNote, ReportPage, ReportRow, ReportRows } from './report-kit';
import { type ReportScope, useProductsReport } from './queries';
import { csvMoney, ExportCsv, moneyShort, shareWords, TabEmpty, TabPending } from './tab-kit';

type Item = ProductsReport['items'][number];
type Pass = ProductsReport['passes'][number];

export function ProductsTab({ scope }: { readonly scope: ReportScope }): ReactNode {
  const { t } = useTranslation('console');
  const report = useProductsReport(scope, true);
  if (report.data === undefined) return <TabPending query={report} unavailable={t('reports.unavailable')} forbidden={t('reports.notPermittedFinancial')} />;
  const data = report.data;
  if (data.items.length === 0 && data.passes.length === 0 && data.play.passes.amount === 0 && data.play.rateCard.amount === 0) {
    return <TabEmpty title={t('reports.emptyTitle')} description={t('reports.products.empty')} />;
  }
  return <Products report={data} />;
}

function Products({ report }: { readonly report: ProductsReport }): ReactNode {
  const { t } = useTranslation('console');
  const format = useVenueFormatter();
  const short = (amount: number) => moneyShort(format, amount, report.currency);
  const itemsNet = report.items.reduce((sum, item) => sum + item.net.amount, 0);
  const passesSold = report.passes.reduce((sum, pass) => sum + pass.sold, 0);
  const passRevenue = report.passes.reduce((sum, pass) => sum + pass.revenue.amount, 0);
  const playTotal = report.play.passes.amount + report.play.rateCard.amount;
  const items = report.items.filter((item) => item.net.amount > 0 || item.quantity > 0);
  const passes = [...report.passes].filter((pass) => pass.sold + pass.granted > 0).sort((a, b) => b.sold - a.sold || b.revenue.amount - a.revenue.amount);

  const itemColumns: readonly TableColumn<Item>[] = [
    { key: 'name', header: t('reports.products.item'), cell: (row) => row.name },
    { key: 'kind', header: t('reports.products.kind'), cell: (row) => <span className="console-muted">{t(`reports.products.kindName.${row.kind}`)}</span> },
    { key: 'quantity', header: t('reports.products.quantity'), numeric: true, cell: (row) => <span className="console-num">{format.number(row.quantity)}</span> },
    { key: 'orders', header: t('reports.products.orders'), numeric: true, cell: (row) => <span className="console-num">{format.number(row.orders)}</span> },
    { key: 'net', header: t('reports.products.net'), numeric: true, cell: (row) => <span className="console-money">{format.money(row.net)}</span> },
  ];
  const passColumns: readonly TableColumn<Pass>[] = [
    { key: 'name', header: t('reports.products.pass'), cell: (row) => row.name },
    { key: 'sold', header: t('reports.products.sold'), numeric: true, cell: (row) => <span className="console-num">{format.number(row.sold)}</span> },
    { key: 'granted', header: t('reports.products.granted'), numeric: true, cell: (row) => <span className="console-num">{format.number(row.granted)}</span> },
    { key: 'revenue', header: t('reports.products.passRevenue'), numeric: true, cell: (row) => <span className="console-money">{format.money(row.revenue)}</span> },
  ];

  return (
    <ReportPage>
      {report.partial ? <ReportNote>{t('reports.partial')}</ReportNote> : null}
      <ReportKpis label={t('reports.tab.products')}>
        <StatCard label={t('reports.products.itemsNet')} value={<KpiFigure value={itemsNet} format={short} />} hint={t('reports.products.itemsNetHint', { count: report.items.length })} />
        <StatCard label={t('reports.products.passesSold')} value={<KpiFigure value={passesSold} format={(n) => format.number(n)} />} hint={t('reports.products.passesSoldHint', { amount: short(passRevenue) })} />
        <StatCard label={t('reports.products.playPasses')} value={<KpiFigure value={report.play.passes.amount} format={short} />} hint={t('reports.products.playPassesHint')} />
        <StatCard label={t('reports.products.playRateCard')} value={<KpiFigure value={report.play.rateCard.amount} format={short} />} hint={t('reports.products.playRateCardHint')} />
      </ReportKpis>

      <ReportGrid layout="lead">
        <ReportCard title={t('reports.products.topItems')} description={t('reports.products.topItemsHint')}>
          {items.length === 0 ? (
            <ChartEmpty>{t('reports.products.noItems')}</ChartEmpty>
          ) : (
            <BarChart
              label={t('reports.products.topItems')}
              limit={6}
              rows={items.map((item) => ({
                key: `${item.kind}-${item.name}`,
                label: item.name,
                value: item.net.amount,
                display: format.money(item.net),
                meta: `${t('reports.products.quantityShort', { count: item.quantity })} · ${t(`reports.products.kindName.${item.kind}`)}`,
              }))}
            />
          )}
          <ChartTable caption={t('reports.products.allItems')} columns={itemColumns} rows={report.items} rowKey={(row) => `${row.kind}-${row.name}`} empty={t('reports.products.noItems')} />
          <div className="console-rt-actions">
            <ExportCsv
              fileName={`products-${report.from}-${report.to}.csv`}
              header={['item', 'kind', 'quantity', 'orders', 'net', 'currency']}
              rows={report.items.map((item) => [item.name, item.kind, item.quantity, item.orders, csvMoney(item.net), report.currency])}
            />
          </div>
        </ReportCard>
        <ReportCard title={t('reports.products.play')} description={t('reports.products.playHint')}>
          {playTotal <= 0 ? (
            <ChartEmpty>{t('reports.noData')}</ChartEmpty>
          ) : (
            <DonutChart
              label={t('reports.products.play')}
              slices={[
                { key: 'passes', label: t('reports.products.playPasses'), value: report.play.passes.amount, display: format.money(report.play.passes), tone: 1 },
                { key: 'rateCard', label: t('reports.products.playRateCard'), value: report.play.rateCard.amount, display: format.money(report.play.rateCard), tone: 2 },
              ]}
              total={{ display: short(playTotal), label: t('reports.products.playTotal') }}
              percent={(share) => shareWords(format, share)}
            />
          )}
          <ReportRows>
            <ReportRow label={t('reports.products.playDiscounts')} value={`− ${format.money(report.play.discounts)}`} quiet={report.play.discounts.amount === 0} />
          </ReportRows>
        </ReportCard>
      </ReportGrid>

      <ReportCard title={t('reports.products.passes')} description={t('reports.products.passesHint')}>
        {passes.length === 0 ? (
          <ChartEmpty>{t('reports.products.noPasses')}</ChartEmpty>
        ) : (
          <BarChart
            label={t('reports.products.passes')}
            limit={8}
            rows={passes.map((pass) => ({
              key: pass.name,
              label: pass.name,
              value: pass.sold,
              display: t('reports.products.soldCount', { count: pass.sold }),
              meta: pass.granted > 0 ? t('reports.products.passMetaGranted', { amount: format.money(pass.revenue), count: pass.granted }) : t('reports.products.passMeta', { amount: format.money(pass.revenue) }),
            }))}
          />
        )}
        <ChartTable caption={t('reports.products.passes')} columns={passColumns} rows={report.passes} rowKey={(row) => row.name} empty={t('reports.products.noPasses')} />
      </ReportCard>
    </ReportPage>
  );
}
